import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { I18n, I18nProvider } from '@fiverr-private/fiverr_context';

import CheckoutFormHOC from '../shared/CheckoutFormHOC';
import SafePureComponent from '../shared/SafePureComponent';
import { shouldShowSelectPackage } from '../../utils/shouldShowSelectPackage';
import { enrichFeaturesRowData, preparePricingFactors } from '../../utils/pricingFactors';
import { calculateServiceName, enrichServices, groupFeaturesByService } from '../../utils/studios';
import { flattenCalculators } from '../../utils/calculator';
import PackageType from './PackageType';
import Description from './Description';
import Revisions from './Revisions';
import DeliveryTime from './DeliveryTime';
import CalculatorRow from './CalculatorRow';
import SelectPackage from './SelectPackage';
import PricingFactorRow from './PricingFactorRow';
import ServiceNameRow from './ServiceNameRow';

import './style.scss';

const renderServiceCalculator = (calculators) => {
    if (calculators) {
        return <CalculatorRow calculatorRowData={calculators}/>;
    }
};

const renderServiceFeatures = (features) => {
    if (features) {
        return features.map((feature) => <PricingFactorRow key={feature.id} {...feature}/>);
    }
};

const renderServices = (packageList, services) => {
    const enrichedServices = enrichServices(services, packageList);
    const enrichedFeatures = enrichFeaturesRowData(packageList);
    const featuresByService = groupFeaturesByService(enrichedServices, enrichedFeatures);

    return (
        enrichedServices.map((({ id, subCategoryId, nestedSubCategoryId, calculators }) => (
            <I18nProvider key={id}>
                {({ i18n }) => [
                    <ServiceNameRow key={id} name={calculateServiceName(i18n, subCategoryId, nestedSubCategoryId)}/>,
                    renderServiceCalculator(calculators),
                    renderServiceFeatures(featuresByService[id])
                ]}
            </I18nProvider>
        )))
    );
};

const renderPricingFactorRows = (packageList) => {
    const pricingFactors = preparePricingFactors(packageList);

    return (
        pricingFactors.map(((pricingFactor) =>
            <PricingFactorRow key={pricingFactor.id} {...pricingFactor}/>))
    );
};

const renderSingleCalculatorRow = (packageList) => {
    const calculatorRowData = flattenCalculators(packageList);

    if (calculatorRowData.length !== packageList.length) {
        return;
    }

    return (
        <CalculatorRow calculatorRowData={calculatorRowData}/>
    );
};

const Table = ({ packageList, services }, { general }) => {
    const { isStudio } = general;
    const showSelectPackage = shouldShowSelectPackage(general);

    return (
        <div className="gig-page-packages-table">
            <h2 className="section-title"><I18n k={'gig_page_perseus.packages_table.title'}/></h2>
            <table>
                <colgroup>
                    <col/>
                    {packageList.map(({ id }) => <col key={id}/>)}
                </colgroup>
                <tbody>
                    <PackageType/>
                    <Description/>
                    {isStudio && <Revisions/>}
                    {isStudio && <DeliveryTime showSelection={false}/>}
                    {isStudio && renderServices(packageList, services)}
                    {!isStudio && renderPricingFactorRows(packageList)}
                    {!isStudio && <Revisions/>}
                    {!isStudio && <DeliveryTime/>}
                    {!isStudio && renderSingleCalculatorRow(packageList)}
                    {showSelectPackage && <SelectPackage/>}
                </tbody>
            </table>
        </div>
    );
};

Table.contextTypes = {
    general: PropTypes.object
};

const mapStateToTableProps = ({ packages }) => {
    const { packageList } = packages;

    return {
        packageList
    };
};

const TableConnect = connect(mapStateToTableProps)(Table);

const PackagesTable = ({ selectedPackageId, services }) => (
    <CheckoutFormHOC className="gig-page-packages-table-form"
        selectedPackageId={selectedPackageId}
        source="Packages Table">
        <TableConnect services={services}/>
    </CheckoutFormHOC>
);

PackagesTable.propTypes = {
    selectedPackageId: PropTypes.number,
    services: PropTypes.array
};

const mapStateToTableFormProps = ({ packages }) => ({
    selectedPackageId: packages.selectedPackageId
});
const PackagesTableWrapper = connect(mapStateToTableFormProps)(PackagesTable);

export { PackagesTableWrapper, Table };
export default SafePureComponent(PackagesTableWrapper);
